import { createSlice } from "@reduxjs/toolkit";
import { PartnerDetailType } from "./Types";
import { columnFilterLabelType, postJoinedTablesFieldsDataType, SaveReportPayload } from "../../saga/partner-details";

type initialStateType = {
  allPartners: PartnerDetailType[];
  partnerDropdownOptions: { label: string; value: number }[];
  filteredPartners: PartnerDetailType[];
  partnerDetails: PartnerDetailType | null;
  partnerChildren: {
    ID: number;
    partner_id: string;
    partner_name: string;
    partner_email: string;
    status: string;
  }[];
  partnerTypes: {
    value: string;
  }[],
  revenueStream: {
    value: string;
  }[],
  partnerVertical: {
    value: string;
  }[],
  partnerTier: {
    value: string;
  }[],
  // User roles state added for getUserRolesSaga
  userRoles: {
    hidRoles: string[];
    partnerRoles: string[];
  };
  joinedTablesFields: columnFilterLabelType[];
  showTableData: any;
  reportFilter: postJoinedTablesFieldsDataType;
  saveReport: SaveReportPayload;
  report_id: number;
  user: number;
  chart: any;
  status: {
    allPartnersLoading: boolean;
    partnerDropdownLoading: boolean;
    partnerDetailsLoading: boolean;
    addPartnerLoading: boolean;
    addPartnerStatus: boolean;
    addPartnerMessage: string;
    updatePartnerLoading: boolean;
    updatePartnerStatus: boolean;
    updatePartnerMessage: string;
    linkPartnerLoading: boolean;
    linkPartnerStatus: boolean;
    partnerChildrenLoading: boolean;
    togglePartnerEnableLoading: boolean;
    togglePartnerEnableStatus: boolean;
    filteredPartnersLoading: boolean;
    deletePartnerLoading: boolean;
    deletePartnerStatus: boolean;
    deletePartnerMessage: string;
    userRolesLoading: boolean;
    userRolesError: boolean;
    joinedTablesFieldsError: boolean;
    joinedTablesFieldsLoading: boolean;
    exportReportLoading: boolean;
  };
};

var initialState: initialStateType = {
  allPartners: [],
  partnerDropdownOptions: [],
  filteredPartners: [],
  partnerDetails: null,
  partnerChildren: [],
  partnerTypes: [],
  revenueStream: [],
  partnerVertical: [],
  partnerTier: [],
  report_id: 1,
  user: 0,
  userRoles: {
    hidRoles: [],
    partnerRoles: [],
  },
  joinedTablesFields: [],
  showTableData: [],
  reportFilter: {
    report_title: "",
    selectColumns: [],
    filters: [],
    chart: {
      type: "",
      title: "",
      xAxis: "",
      yAxis: "",
      dateField: "",
      dateRange: {
        from: "",
        to: "",
      },
    },
  },
  saveReport: {
    report_title: "",
    user: 0,
    category_id: 0,
    definition: {
      selectColumns: [],
      filters: [],
      chart: {
        type: "",
        title: "",
        xAxis: "",
        yAxis: "",
        dateField: "",
        dateRange: {
          from: "",
          to: ""
        }
      }
    }
  },
  chart: {},
  status: {
    allPartnersLoading: false,
    partnerDropdownLoading: false,
    partnerDetailsLoading: false,
    addPartnerLoading: false,
    addPartnerStatus: false,
    addPartnerMessage: "",
    updatePartnerLoading: false,
    updatePartnerStatus: false,
    updatePartnerMessage: "",
    linkPartnerLoading: false,
    linkPartnerStatus: false,
    partnerChildrenLoading: false,
    togglePartnerEnableLoading: false,
    togglePartnerEnableStatus: false,
    filteredPartnersLoading: false,
    deletePartnerLoading: false,
    deletePartnerStatus: false,
    deletePartnerMessage: "",
    userRolesLoading: false,
    userRolesError: false,
    joinedTablesFieldsError: false,
    joinedTablesFieldsLoading: false,
    exportReportLoading: false,
  },
};

const partnerDetailsSlice = createSlice({
  name: "partner-details",
  initialState: initialState,
  reducers: {
    setAllPartners: (state, action) => {
      state.allPartners = action.payload;
    },
    setAllPartnersLoading: (state, action) => {
      state.status.allPartnersLoading = action.payload;
    },
    setPartnerDropdownOptions: (state, action) => {
      state.partnerDropdownOptions = action.payload;
    },
    setPartnerDropdownLoading: (state, action) => {
      state.status.partnerDropdownLoading = action.payload;
    },
    setPartnerDetails: (state, action) => {
      state.partnerDetails = action.payload;
    },
    setPartnerDetailsLoading: (state, action) => {
      state.status.partnerDetailsLoading = action.payload;
    },
    setAddPartnerLoading: (state, action) => {
      state.status.addPartnerLoading = action.payload;
    },
    setAddPartnerStatus: (state, action) => {
      state.status.addPartnerStatus = action.payload;
    },
    setAddPartnerMessage: (state, action) => {
      state.status.addPartnerMessage = action.payload;
    },
    setUpdatePartnerLoading: (state, action) => {
      state.status.updatePartnerLoading = action.payload;
    },
    setUpdatePartnerStatus: (state, action) => {
      state.status.updatePartnerStatus = action.payload;
    },
    setUpdatePartnerMessage: (state, action) => {
      state.status.updatePartnerMessage = action.payload;
    },
    setPartnerTypes: (state, action) => {
      state.partnerTypes = action.payload;
    },
    setRevenueStream: (state, action) => {
      state.revenueStream = action.payload;
    },
    setPartnerVertical: (state, action) => {
      state.partnerVertical = action.payload;
    },
    setPartnerTier: (state, action) => {
      state.partnerTier = action.payload;
    },
    setLinkPartnerLoading: (state, action) => {
      state.status.linkPartnerLoading = action.payload;
    },
    setLinkPartnerStatus: (state, action) => {
      state.status.linkPartnerStatus = action.payload;
    },
    setPartnerChildren: (state, action) => {
      state.partnerChildren = action.payload;
    },
    setPartnerChildrenLoading: (state, action) => {
      state.status.partnerChildrenLoading = action.payload;
    },
    setTogglePartnerEnableLoading: (state, action) => {
      state.status.togglePartnerEnableLoading = action.payload;
    },
    setTogglePartnerEnableStatus: (state, action) => {
      state.status.togglePartnerEnableStatus = action.payload;
    },
    setFilteredPartners: (state, action) => {
      state.filteredPartners = action.payload;
    },
    setFilteredPartnersLoading: (state, action) => {
      state.status.filteredPartnersLoading = action.payload;
    },
    setDeletePartnerLoading: (state, action) => {
      state.status.deletePartnerLoading = action.payload;
    },
    setDeletePartnerStatus: (state, action) => {
      state.status.deletePartnerStatus = action.payload;
    },
    setDeletePartnerMessage: (state, action) => {
      state.status.deletePartnerMessage = action.payload;
    },
    // -------------------- User Roles --------------------
    setUserRolesLoading: (state, action) => {
      state.status.userRolesLoading = action.payload;
    },
    setUserRoles: (state, action) => {
      state.userRoles = action.payload;
      state.status.userRolesError = false;
    },
    setUserRolesError: (state, action) => {
      state.status.userRolesError = action.payload;
    },

    setJoinedTablesFieldsLoading: (state, action) => {
      state.status.joinedTablesFieldsLoading = action.payload;
    },
    setJoinedTablesFields: (state, action) => {
      state.joinedTablesFields = [...action.payload];
      state.status.joinedTablesFieldsError = false;
    },
    setJoinedTablesFieldsError: (state, action) => {
      state.status.joinedTablesFieldsError = action.payload;
    },

    setReportFilterTitleAction: (state, action) => {
      state.reportFilter.report_title = action.payload;
    },
    setReportFilterColumnAction: (state, action) => {
      state.reportFilter.selectColumns = action.payload;
    },
    setReportFilterAction: (state, action) => {
      state.reportFilter.filters = action.payload;
    },
    setReportFilterChartAction: (state, action) => {
      state.reportFilter.chart = action.payload;
    },
    setChartDataAction: (state, action) => {
      state.chart = action.payload;
    },
    setShowTableDataAction: (state, action) => {
      state.showTableData = action.payload;
    },
    setsaveReportDataAction: (state, action) => {
      state.saveReport = action.payload;
    },
    setLastSavedReportId: (state, action) => {
      state.report_id = action.payload;
    },
    resetSaveReportStatus: (state) => {
      // state.report_id = 0;
      // state.status.updatePartnerMessage = "";
      // state.status.updatePartnerStatus = false;
    },

    setExportReportLoading: (state, action) => {
      state.status.exportReportLoading = action.payload;
    },
  },
});

export const partnerDetailsSliceReducer = partnerDetailsSlice.reducer;
export const partnerDetailsSliceAction = partnerDetailsSlice.actions;

// Export selectors 
export * from './selectors';